import React from 'react';
import { Shield } from 'lucide-react';
import { Panel } from './ui/TacticalPrimitives';

interface DisclaimerProps {
  variant?: 'default' | 'compact' | 'inline' | 'footer';
  className?: string;
}

const currentYear = new Date().getFullYear();

const FULL_TEXT = `This material is prepared by Scientific Analytics Alliance ("SAA") for informational and analytical purposes only. It does not constitute and shall not be construed as investment advice, a solicitation or an offer to buy or sell any securities, financial instruments, or digital assets. The information presented may include forward-looking statements, estimates, and model outputs that are inherently uncertain. Digital assets are highly volatile and speculative in nature. Past performance is not indicative of future results. Recipients should conduct independent research and consult qualified legal, tax, and financial advisors before making any investment decisions. SAA does not accept any liability for loss or damage arising directly or indirectly from the use of, or reliance on, this material. Data provenance and methodology are documented in the Institutional Report.`;

const COMPACT_TEXT = `For professional and institutional use only. This material does not constitute investment, legal, or tax advice. Data may be estimated or model-derived. Refer to the Institutional Report for data provenance and methodology.`;

const FOOTER_TEXT = `\u00A9 ${currentYear} Scientific Analytics Alliance. For professional use only. Not investment or legal advice. Digital assets involve substantial risk of loss. Past performance is not indicative of future results. SAA accepts no liability for losses arising from use of this information.`;

const INLINE_TEXT = `For professional use only. Not investment or legal advice. Data may be estimated.`;

const Disclaimer: React.FC<DisclaimerProps> = ({
  variant = 'default',
  className = '',
}) => {
  if (variant === 'footer') {
    return (
      <div className={`text-[10px] text-cmd-textDim leading-relaxed tracking-[0.08em] uppercase ${className}`}>
        {FOOTER_TEXT}
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`text-[10px] text-cmd-textDim tracking-[0.08em] uppercase flex items-center gap-2 ${className}`}>
        <Shield className="w-3 h-3 text-cmd-warning shrink-0" />
        <span>{INLINE_TEXT}</span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`border-t border-[color:rgb(var(--c-border-rgb)/0.95)] pt-3 mt-3 ${className}`}>
        <div className="flex items-start gap-2">
          <Shield className="w-3.5 h-3.5 text-cmd-warning shrink-0 mt-0.5" />
          <p className="text-[10px] text-cmd-textDim leading-relaxed tracking-[0.08em] uppercase">
            {COMPACT_TEXT}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Panel className={`p-5 bg-cmd-surface ${className}`}>
      <div className="flex items-start gap-3">
        <div className="bg-[color:rgb(var(--c-alert-amber-rgb)/0.08)] border border-[color:rgb(var(--c-alert-amber-rgb)/0.16)] p-1.5 shrink-0 rounded-[2px]">
          <Shield className="w-4 h-4 text-cmd-warning" />
        </div>
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-[0.16em] text-cmd-textDim mb-2 font-mono">
            Important disclosure
          </div>
          <p className="text-[11px] text-cmd-textMuted leading-relaxed">
            {FULL_TEXT}
          </p>
        </div>
      </div>
    </Panel>
  );
};

export default Disclaimer;
